import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ApolloError, useMutation, useQuery } from "@apollo/client";
import { MEETING_TODO_DETAIL } from "../../api/query";
import { useAuth } from "../../context/AuthContext";
import ioniconaaddoutline from "../../assets/dashboard/ioniconaaddoutline.svg";
import Loader from "../../components/common/Loader";
import { format } from "date-fns";
import DeleteMeeting from "../../components/dashboard/DeleteMeeting";
import { DELETE_BULK_MEETINGS, MEETING_ACTIONS, TODO_ACTIONS } from "../../api/mutation";
import { showError, showSuccess } from "../../components/common/ToastNotification";
import dftarrowback from "../../assets/task/back_arrow.svg"
import calIcon from "../../assets/meeting/calendar.svg"
import clockMeeting from "../../assets/meeting/clock-meeting.svg"
import meetingLink from "../../assets/meeting/link-2.svg"
import userMeeting from "../../assets/meeting/user-meeting.svg"
import checkIcon from "../../assets/meeting/tick-circle.svg"
import closeIcon from "../../assets/meeting/add-circle.svg"
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import InputsNew from "../../components/common/InputsNew";
import AddTodoPopup from "../../components/meeting/AddTodoPopup";
import BreadCumb from "../../components/common/Breadcrumb";

interface AgendaItem {
    id: string;
    title: string;
    is_discussed: boolean;
}

interface Participant {
    id: string;
    is_attended: boolean;
    absent_note: string | null;
    member_name: string;
    employee_id: number;
}

interface ExternalParticipant {
    id: string;
    is_attended: boolean;
    absent_note: string | null;
    name: string;
    email: number;
}

// Validation schema
const addMeetingSchema = yup.object().shape({
    meetingOutcome: yup
        .string()
        .required("This field is required")
        .test(
            "no-only-spaces",
            "This field is required",
            (value: any) => value && value.trim().length > 0
        )
});

const now = new Date();
const currentDateAndTime = format(now, "yyyy-MM-dd'T'HH:mm:ssxxx").replace(/(\+|\-)\d{2}:\d{2}$/, "Z");

const MeetingDetail: React.FC = () => {
    const navigate = useNavigate()
    const location = useLocation();
    const state = location.state as {
        meetingID?: string | null,
        startMeetDate?: string | null,
        endMeetDate?: string | null
    };

    const { meetingID, startMeetDate, endMeetDate } = state

    const { userInfo } = useAuth();

    const employeeID = userInfo?.employee_id || '';
    const organizationID = userInfo?.agencies?.id || '';
    const [isDeletePopupOpen, setIsDeletePopupOpen] = useState(false);
    const [deletedId, setDeletedId] = useState<any>(null);
    const [addTodoPopup, setAddTodoPopup] = useState(false)

    //api
    const {
        loading,
        data: meetingsData,
        refetch: refetchMeeting,
    } = useQuery(MEETING_TODO_DETAIL, {
        variables: {
            start_date: startMeetDate,
            end_date: endMeetDate,
            agency_id: Number(organizationID),
            meeting_id: Number(meetingID)
        },
        fetchPolicy: "no-cache",
        skip: !organizationID || !startMeetDate || !endMeetDate || !meetingID,
    });

    const [updateTodoStatus, { loading: updateToDoLoading }] = useMutation(TODO_ACTIONS, {
        refetchQueries: [MEETING_TODO_DETAIL], // optional: refresh list after update
        awaitRefetchQueries: true
    });

    const [meetingActions, { loading: meetingActionsLoading }] = useMutation(MEETING_ACTIONS, {
        fetchPolicy: 'no-cache',
    })

    const [deleteMeeting, { loading: deleteloading }] = useMutation(DELETE_BULK_MEETINGS);



    const [agenda, setAgenda] = useState<AgendaItem[]>([]);

    const [participants, setParticipants] = useState<Participant[]>([]);
    const [externalParticipants, setExternalParticipants] = useState<ExternalParticipant[]>([]);
    const [showModal, setShowModal] = useState(false);
    const [selectedParticipant, setSelectedParticipant] = useState<Participant | null>(null);
    const [selectedExternalParticipant, setSelectedExternalParticipant] = useState<ExternalParticipant | null>(null);
    const [absentNote, setAbsentNote] = useState("");


    const completedCount = agenda.filter((a) => a.is_discussed).length;
    // const presentCount = participants.filter((p) => p.status === "Present").length;

    const meeting = meetingsData?.get_meetings?.meetings[0]
    const hostID = meeting?.host?.employee_id

    const isHost = hostID == employeeID;

    const {
        register,
        handleSubmit,
        setValue,
        formState: { errors },
    } = useForm<any>({
        resolver: yupResolver(addMeetingSchema)
    });

    // IMPORTANT:
    // MEETING_TODO_DETAIL is refetched after a todo is created/updated.
    // Do not re-initialize the editable agenda/outcome fields on every refetch,
    // otherwise unsaved checkbox changes and Meeting Outcome get overwritten
    // by the old values returned from the API.
    const initializedMeetingIdRef = useRef<string | number | null>(null);

    useEffect(() => {
        if (
            meetingsData?.get_meetings?.status_code === 200 &&
            meetingsData?.get_meetings?.meetings?.length > 0
        ) {
            const meeting = meetingsData.get_meetings.meetings[0];
            const currentMeetingId = meeting?.id ?? meetingID;

            // Initialize editable fields only when this meeting is loaded.
            // Later refetches will still update `meeting` (including todos),
            // but will not clear the user's unsaved changes.
            if (initializedMeetingIdRef.current !== currentMeetingId) {
                initializedMeetingIdRef.current = currentMeetingId;

                setAgenda(meeting.agendas || []);
                setValue("meetingOutcome", meeting?.meeting_outcome || "");
            }

            // These can safely follow the latest API response.
            // setParticipants(meeting?.members || []);
setParticipants((prevParticipants) => {
    const previousById = new Map(
        prevParticipants.map((p: any) => [String(p.id), p])
    );

    return (meeting?.members || []).map((m: any) => {
        const previous = previousById.get(String(m.id));

        // If participant already exists in local state, preserve
        // the user's current Present/Absent selection.
        if (previous) {
            return {
                ...m,
                is_attended: previous.is_attended,
                absent_note: previous.absent_note,
                is_attended_temp: previous.is_attended_temp,
            };
        }

        // First load: use API value.
        return {
            ...m,
            is_attended: !!m.is_attended_temp,
            is_attended_temp: !!m.is_attended_temp,
            absent_note: m.is_attended_temp
                ? m.absent_note
                : "Not joined check with Automation",
        };
    });
});

// Same protection for external participants.
setExternalParticipants((prevParticipants) => {
    const previousById = new Map(
        prevParticipants.map((p: any) => [String(p.id), p])
    );

    return (meeting?.external_members || []).map((m: any) => {
        const previous = previousById.get(String(m.id));

        if (previous) {
            return {
                ...m,
                is_attended: previous.is_attended,
                absent_note: previous.absent_note,
            };
        }

        return {
            ...m,
            is_attended: !!m.is_attended,
        };
    });
});
        }
    }, [meetingsData, meetingID, setValue]);

    const toggleAgenda = (id: string) => {
        setAgenda((prev) =>
            prev.map((item: AgendaItem) =>
                item.id === id ? { ...item, is_discussed: !item.is_discussed } : item
            )
        );
    };

const handleMarkPresent = (id: string) => {
    setParticipants((prev) =>
        prev.map((p) =>
            p.id === id
                ? { ...p, is_attended: true, is_attended_temp: true, absent_note: null }  // 👈
                : p
        )
    );
    const participant = participants.find((p) => p.id === id);
    if (participant) console.log("Marked Present:", participant.employee_id);
};

    const handleMarkAbsent = (participant: Participant) => {
        setSelectedParticipant(participant);
        setAbsentNote("");
        setShowModal(true);
    };

    const handleMarkExternalPresent = (id: string) => {
        setExternalParticipants((prev) =>
            prev.map((p) =>
                p.id === id ? { ...p, is_attended: true, absent_note: null } : p
            )
        );
        const participant = participants.find((p) => p.id === id);
        if (participant) console.log("Marked Present:", participant.employee_id);
    };

    const handleMarkExternalAbsent = (participant: ExternalParticipant) => {
        setSelectedExternalParticipant(participant);
        setAbsentNote("");
        setShowModal(true);
    };

const saveAbsentNote = () => {
    if (!selectedParticipant) return;

    setParticipants((prev) =>
        prev.map((p) =>
            p.id === selectedParticipant.id
                ? { ...p, is_attended: false, is_attended_temp: false, absent_note: absentNote }  // 👈
                : p
        )
    );

    setShowModal(false);
    setSelectedParticipant(null);
    setAbsentNote("");
};

    const saveExternalAbsentNote = () => {
        if (!selectedExternalParticipant) return;

        setExternalParticipants((prev) =>
            prev.map((p) =>
                p.id === selectedExternalParticipant.id
                    ? { ...p, is_attended: false, absent_note: absentNote }
                    : p
            )
        );

        setShowModal(false);
        setSelectedExternalParticipant(null);
        setAbsentNote("");
    };

    function handleDeletePopUp(id: any) {
        if (id) {
            setIsDeletePopupOpen(true);
            setDeletedId(id);
        }
    }

    const closeDeletePopup = () => {
        setIsDeletePopupOpen(false);
    };

    const deleteMeetingFunction = (formData: any) => {
        // Find the meeting that's being deleted
        // const meetingToDelete = getMeetings?.find(
        //     (meeting: any) => meeting.id === Number(deletedId)
        // );

        return deleteMeeting({
            variables: {
                deleted_by: Number(employeeID),
                meeting_ids: [Number(deletedId)],
            },
        })
            .then((response: any) => {
                const { message, status_code } = response.data.delete_bulk_meetings;

                setDeletedId(null);
                setIsDeletePopupOpen(false);
                if (status_code == 200) {
                    showSuccess(message);
                    navigate(-1)

                    // Only send cancellation email if meeting is in the future
                    // if (meeting) {
                    //     const meetingTime = new Date(meeting?.start_datetime);
                    //     const now = new Date();

                    //     // Send email only if meeting is in the future
                    //     // if (meetingTime > now) {
                    //     //   sendDeleteMeetingEmail(meetingToDelete);
                    //     // }
                    // }

                    // meetingRefetch({
                    //     variables: {
                    //         start_date: startDate,
                    //         end_date: endDate,
                    //         agency_id: Number(agencyId)
                    //         // employee_uuid: userUUID,
                    //         // page: currentPage,
                    //         // page_size: pageSize,
                    //     },
                    // });
                    return true;
                }
            })
            .catch((error: any) => {
                if (error instanceof ApolloError) {
                    showError(error.message);
                    return false;
                } else {
                    showError(error);
                    return false;
                }
            });
    };

    async function onSubmit(data: any, isEmailSend: boolean) {
        if (completedCount !== agenda?.length) {
            showError("Please discuss all agenda items before submitting.");
            return;
        }

        if (!meeting?.todos?.length) {
            showError("Please assign at least one to-do before completing the meeting.");
            return;
        }

        const parsedAgendas = agenda.map(({ __typename, id, ...rest }: any) => ({
            ...rest,
            id: parseInt(id)
        }));

        const parsedParticipants = participants.map(({ __typename, member_name, id, employee_id, ...rest }: any) => ({
            ...rest,
            id: parseInt(id),
            member_id: employee_id
        }));

        const parsedExternalParticipants = externalParticipants.map(({ __typename, agency_id, id, ...rest }: any) => ({
            ...rest,
            id: parseInt(id)
        }));


        try {
            const response = await meetingActions({
                variables: {
                    meeting_id: Number(meetingID),
                    outcome: data.meetingOutcome,
                    agendas: parsedAgendas,
                    members: parsedParticipants,
                    external_members: parsedExternalParticipants,
                    is_email_send: isEmailSend,
                }
            });
            if (response.data.meeting_actions.status_code === 200 || response.data.meeting_actions.status_code === 201) {
                showSuccess(response.data.meeting_actions.message)
                navigate(-1)
            }
            if (response.data.meeting_actions.status_code === 400) {
                showError(response.data.meeting_actions.message)
            }
        } catch (error) {
            if (error instanceof ApolloError) {
                showError(error.message)
            }
        }
    }

    function onBackClick() {
        // navigate("/dashboard")
        navigate(-1)
    }

    return (
        <>
            <form onSubmit={handleSubmit((data) => onSubmit(data, false))} className="w-full relative overflow-hidden flex flex-col items-start justify-start min-w-full max-w-full leading-[normal] tracking-[normal] text-left text-xl text-text-text-primary font-body-reg mq950:max-w-full mq450:max-w-full mq1425:max-w-full mq1900:max-w-full">
                <div id="cts-respon-p-dv" className="meetringdetails-section self-stretch overflow-hidden flex flex-row flex-wrap items-start justify-center py-40 px-20 mq575:px-5 box-border max-w-full mq950:pt-[34px] mq950:pb-[34px] mq950:box-border mq1425:box-border">

                    <div className="w-full flex flex-col items-start justify-start gap-[24px] max-w-full mq1425:max-w-full">

                               <BreadCumb />
                        <div className="self-stretch flex flex-row items-center justify-start gap-[5px] max-w-full">
                            <img
                                className="h-6 w-6 relative overflow-hidden shrink-0  dark-white-img cursor-pointer"
                                loading="lazy"
                                alt=""
                                src={dftarrowback}
                                onClick={onBackClick}
                            />
                            <h3 className="m-0 flex-1 relative text-inherit font-bold font-inherit inline-block max-w-[calc(100%_-_29px)] mq450:text-base">
                                Meeting Details
                            </h3>
                        </div>
                        <div className="m-0 self-stretch meeting-details-main  rounded-radius-radius-lg bg-surface-bg-blue darkborder-b-remove flex flex-col items-start justify-start p-8 mq575:p-3 box-border gap-spacing-lg max-w-full mq825:pt-5 mq825:pb-5 mq825:box-border mq1425:pt-[21px] mq1425:pb-[21px] mq1425:box-border">

                            {/* Header */}
                            <div className="self-stretch flex flex-row items-center justify-between gap-[5px] w-full mq575:grid">
                                <h1 className="text-xl font-semibold flex-1">{meeting?.title && meeting?.title}</h1>
                                {((isHost) && (meeting?.is_email_send !== true)) && (
                                    // {(isHost) && (
                                    <div className="grid justify-end">
                                        <span className="gap-[16px] flex text-sm flex-1">
                                            <button className="cursor-pointer editmeetign_btn [border:none] py-2.5 h-[44px] mq575:h-[35px] px-[42px] mq575:px-[24px] mq575:py-[12.5px] rounded-radiuscomponent-button flex flex-row items-center justify-center gap-[12px]"
                                                type="button"
                                                onClick={() => {
                                                    navigate("/edit_meeting", {
                                                        state: {
                                                            meetingID: meetingID,
                                                            startMeetDate: startMeetDate,
                                                            endMeetDate: endMeetDate
                                                        }
                                                    })
                                                }}
                                            >
                                                <span className="relative text-sm font-body-xs-semi-bold text-surface-bg-primary text-center grid">Edit Meeting</span>
                                            </button>

                                            <button className="cursor-pointer deletemeetign_btn [border:none] py-2.5 h-[44px] mq575:h-[35px] px-[42px] mq575:px-[24px] mq575:py-[12.5px] rounded-radiuscomponent-button flex flex-row items-center justify-center gap-[12px]"
                                                type="button"
                                                onClick={() =>
                                                    handleDeletePopUp(meeting?.id)
                                                }
                                            >
                                                <span className="relative text-sm font-body-xs-semi-bold text-surface-bg-primary text-center grid">Delete Meeting</span>
                                            </button>
                                        </span>
                                    </div>
                                )}
                            </div>
                            <div className="self-stretch flex flex-wrap items-center justify-start gap-[5px] w-[60%] mq991:w-full">
                                <div className="flex gap-[10px] w-full mq991:grid">
                                    <p className="my-1 text-sm flex-1 dark:text-white flex items-center gap-[8px]">
                                        <img src={calIcon} className="w-5 h-5" alt="" />
                                        <span>Date:</span>
                                        {meeting?.start_datetime && format(
                                            new Date(meeting?.start_datetime),
                                            "MM-dd-yyyy"
                                        )}
                                    </p>
                                    <p className="my-1 flex-1 text-sm dark:text-white flex items-center gap-[8px]">
                                        <img src={clockMeeting} className="w-5 h-5" alt="" />
                                        <span>Time:</span>
                                        {meeting?.start_datetime && `${format(
                                            new Date(meeting?.start_datetime),
                                            "hh:mm a"
                                        )} - ${format(
                                            new Date(meeting?.end_datetime),
                                            "hh:mm a"
                                        )}`}
                                    </p>

                                </div>
                                <div className="flex gap-[10px] w-full  mq991:grid">
                                    <p className="my-1 flex-1 text-sm dark:text-white flex items-center gap-[8px]">
                                        <img src={userMeeting} className="w-5 h-5" alt="" />
                                        <span>Host:</span> {meeting?.host_name && meeting?.host_name}</p>
                                    <p className="my-1 flex-1 text-sm dark:text-white flex items-center gap-[8px]">
                                        <img src={meetingLink} className="w-5 h-5" alt="" />
                                        <span>Meeting Link: </span>
                                        <a
                                            href={meeting?.meeting_link_string}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-blue-600 underline"
                                        >
                                            {meeting?.meeting_link_string}
                                        </a>
                                    </p>

                                </div>
                                <div className="flex gap-[10px] w-full  mq991:grid">
                                    <p className="my-1 flex-1 text-sm dark:text-white flex items-center gap-[8px]">
                                        <img src={userMeeting} className="w-5 h-5" alt="" />
                                        <span>Meeting Status:</span> {meeting?.status_name && meeting?.status_name}</p>
                                </div>
                            </div>
                            <div className="self-stretch flex flex-row  items-start justify-start py-0 pr-0.5 pl-0 box-border gap-[20px_18px] w-full text-base tablet-view-blg-dv">
                                {/* Agenda Section */}
                                <div className=" lightgary_bg_dv rounded bg-whitesmoke-100 dark_theme_box  min-w-[70%] flex flex-col items-start justify-start gap-[20px]  max-w-full  mq575:min-w-full p-5">
                                    <div className="flex justify-between w-full  mq991:grid mq991:gap-[16px]">
                                        <h2 className="font-semibold my-0">Meeting Agenda</h2>
                                        <div className="rounded-[50px] bg-antiquewhite border-whitesmoke w-fit mq991:py-2 border-solid border-[1px] box-border flex flex-row items-center justify-center  px-4 text-base blue_status_bg_dv">
                                            <p className="text-sm text-gray-500 my-0">
                                                {completedCount}/{agenda.length} completed
                                            </p>
                                        </div>

                                    </div>
                                    <div className="w-full grid gap-[16px] my-[16px] max-h-[370px] pr-2 overflow-y-auto">
                                        {agenda.map((item) => (
                                            <div
                                                key={item.id}
                                                className={`flex items-center py-2.5 px-[15px] border-[1px] border-solid border-surface-border rounded cursor-pointer ${item.is_discussed ? "bg-purple-50" : "bg-white"
                                                    }`}
                                                onClick={() => toggleAgenda(item.id)}
                                            >
                                                <input
                                                    type="checkbox"
                                                    checked={item.is_discussed}
                                                    className="mr-3"
                                                />
                                                <span
                                                    className={
                                                        item.is_discussed
                                                            ? "text-purple-700 font-medium"
                                                            : "text-gray-700"
                                                    }
                                                >
                                                    {item.title}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                    <div className="self-stretch flex flex-col items-start justify-start text-sm">
                                        <div className="self-stretch rounded-radius-md  overflow-hidden flex flex-row items-start justify-start pt-2 shadow-[0px_1px_2px_rgba(16,_24,_40,_0.05)] ">
                                            <InputsNew
                                                contentPlaceholder="Meeting Outcome"
                                                label="Meeting Outcome*"
                                                type="textarea"
                                                register={register('meetingOutcome')}
                                                errorMessage={errors.meetingOutcome}
                                            />
                                        </div>
                                    </div>


                                    <div className="w-full grid gap-[16px]">
                                        <div className="flex-1 flex flex-row items-start justify-between gap-[6px] max-w-full">
                                            <div className="flex items-center gap-3 flex-wrap">
                                                <h2 className="relative font-bold text-xl-dv my-0 capitalize dark:text-white">To Dos</h2>
                                                {meeting?.todos?.length > 0 && (
                                                    <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">
                                                        {new Set(meeting.todos.map((t: any) => t.todo_assigned_user_name)).size} member{new Set(meeting.todos.map((t: any) => t.todo_assigned_user_name)).size !== 1 ? "s" : ""} assigned
                                                    </span>
                                                )}
                                            </div>
                                            <div className="self-stretch flex flex-row items-center justify-start gap-[16px] max-w-full mq950:flex-wrap inner-mobile-action-card-blg-dv">

                                                {/* <div className="cursor-pointer flex flex-col items-start justify-start p-[10px] plus-task-btn taskdash-plush-img rounded ">
                                                    <h5 className="cursor-pointer relative font-bold text-xl-dv my-0 capitalize dark:text-white"
                                                        onClick={() => {
                                                            setAddTodoPopup(true)
                                                        }}
                                                    >
                                                        +
                                                    </h5>
                                                </div> */}
                                                <div className=" cursor-pointer flex flex-col items-start justify-start p-[10px] plus-task-btn taskdash-plush-img rounded ">
                                                    <img
                                                        className="w-4 h-4 relative overflow-hidden shrink-0 cursor-pointer"
                                                        loading="lazy"
                                                        alt=""
                                                        src={ioniconaaddoutline}
                                                        onClick={() => {
                                                            setAddTodoPopup(true)
                                                        }}
                                                    />
                                                </div>
                                            </div>
                                        </div>

                                        <div
                                            className="self-stretch shadow-[0px_1px_4px_rgba(0,_0,_0,_0.25)] mobile-action-card-blg-dv rounded-lg bg-surface-bg-primary flex flex-col items-start justify-center p-4 box-border gap-[6px] max-w-full text-left text-base text-colors-text-black font-body-xs-semi-bold "
                                        >

                                            {meeting?.todos?.length > 0 && (
                                                <div className={`max-w-full flex min-w-full max-h-[560px] overflow-x-hidden overflow-y-auto ${meeting?.toods?.length > 6 ? 'shiftwith-exception-scroll' : ''}`}>
                                                    <div className="grid  min-w-full  gap-[24px]">
                                                        {meeting.todos?.map((t: any) => {
                                                            return (
                                                                <div className="customtask_card_ui_dv  w-full relative rounded cursor-pointer bg-white border-whitesmoke border-solid border-[1px] box-border  flex items-start justify-start pt-4 px-4 pb-4 text-left text-base text-gray font-manrope"
                                                                    key={t?.id}
                                                                >
                                                                    <div className=" w-full relative rounded cursor-pointer   shrink-0 block flex-col items-start justify-start text-left text-base text-gray font-manrope">
                                                                        <div className="self-stretch flex flex-col items-start justify-start">
                                                                            <div className="self-stretch flex flex-col items-start justify-center gap-2 text-dimgray">
                                                                                <div className="self-stretch flex flex-row items-start justify-start gap-2">
                                                                                    <p className="w-[120px] relative inline-block shrink-0 dark:text-white my-0">Due Date:</p>
                                                                                    <p className="relative font-semibold text-gray dark:text-white my-0">
                                                                                        {format(t?.due_date, 'MM-dd-yyyy')}
                                                                                    </p>
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                        <div className="self-stretch flex flex-col items-start justify-start">
                                                                            <div className="self-stretch flex flex-col items-start justify-center gap-2 text-dimgray">
                                                                                <div className="self-stretch flex flex-row items-start justify-start gap-2">
                                                                                    <p className="w-[120px] relative inline-block shrink-0 dark:text-white my-0">Assigned To:</p>
                                                                                    <p className="relative font-semibold text-gray dark:text-white my-0">
                                                                                        {t?.todo_assigned_user_name}
                                                                                    </p>
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                        <div className="grid gap-2 w-full">
                                                                            <hr className="bdr-custom-taskcrad" />
                                                                            <div className="self-stretch flex flex-row items-center justify-start pb-2">
                                                                                <label className="flex items-center gap-2 cursor-pointer">
                                                                                    <input
                                                                                        type="checkbox"
                                                                                        checked={t?.is_completed} // depends on your schema
                                                                                        onChange={(e) => {
                                                                                            updateTodoStatus({
                                                                                                variables: {
                                                                                                    id: parseInt(t?.id),
                                                                                                    is_completed: e.target.checked,
                                                                                                    completed_date: currentDateAndTime
                                                                                                }
                                                                                            })
                                                                                        }}
                                                                                    />
                                                                                    <span className="relative font-semibold text-sm my-0 capitalize tasktitle_card">
                                                                                        {t?.title}
                                                                                    </span>
                                                                                </label>
                                                                            </div>
                                                                            {/* <hr className="bdr-custom-taskcrad" /> */}
                                                                            <div className="self-stretch flex flex-row items-start justify-between gap-0 mt-5">
                                                                                <div
                                                                                    onClick={() => {
                                                                                        navigate("/edit_todo", {
                                                                                            state: {
                                                                                                id: t?.id
                                                                                            }
                                                                                        })
                                                                                    }}
                                                                                    className="rounded-[100px] viewbg-dv right_status_clockout flex flex-row items-center justify-center py-1 px-2 text-sm text-white">
                                                                                    <div className="relative font-semibold">Edit</div>
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            )
                                                        })}
                                                    </div>
                                                </div>
                                            )}

                                            {meeting?.todos?.length === 0 && (
                                                <>No data Available</>
                                            )}
                                        </div >
                                    </div>

                                    {/* <div className="self-stretch flex flex-col items-start justify-start text-sm">
                                        <div className="self-stretch rounded-radius-md  overflow-hidden flex flex-row items-start justify-start pt-2 shadow-[0px_1px_2px_rgba(16,_24,_40,_0.05)] ">
                                            <h5>To Dos</h5>
                                        </div>
                                    </div> */}
                                </div>

                                {/* Participants Section */}
                                <div className="min-w-[24%] shadow-[0px_1px_4px_rgba(0,_0,_0,_0.25)]  action_askcary_section bg-white rounded-lg flex p-4 mq575:p-0 flex-col items-start justify-start gap-[20px]  max-w-[328px] text-sm mq950:min-w-[250px] mq950:max-w-[250px] mq575:min-w-full lightgary_bg_dv rounded bg-whitesmoke-100 dark_theme_box  border-whitesmoke border-solid border-[1px] px-5 pb-5 mq991:px-2 mq991:pb-2 gap-0 ">
                                    <div className="flex justify-between items-center w-full  mq991:grid mq991:gap-[16px]">
                                        <h2 className="font-semibold mb-3 mq991:my-0">Participants</h2>
                                        <p className="rounded-[50px] bg-antiquewhite border-whitesmoke w-fit mq991:py-2 border-solid border-[1px] box-border flex flex-row items-center justify-center  px-4 text-base blue_status_bg_dv">

                                            <p className="text-sm text-gray-500 my-0"> {participants.filter((p) => p.is_attended).length}/{participants.length} present</p>
                                        </p>
                                    </div>
                                    <div className="grid gap-[8px] max-h-[300px] overflow-y-auto w-full">
                                        {participants.map((p) => (
                                            <div key={p.id} className="flex items-center py-[0px] px-[15px] justify-between border-[1px] border-solid border-surface-border rounded cursor-pointer bg-white">
                                                <div>
                                                    <p className="font-medium text-gray-800">{p.member_name}</p>
                                                    {/* <p className="text-sm text-gray-500">Employee ID: {p.employee_id}</p> */}
                                                    {p.absent_note && (
                                                        <p className="text-xs text-red-500 italic">Note: {p.absent_note}</p>
                                                    )}
                                                </div>

                                                <div>
                                                    {p.is_attended ? (
                                                        <span
                                                            className="flex items-center text-green-600 text-sm cursor-pointer present-bg"
                                                            onClick={() => handleMarkAbsent(p)}
                                                        >
                                                            Present
                                                            <img src={checkIcon} className="w-5 h-5 cursor-pointer" alt="" />
                                                        </span>
                                                    ) : (
                                                        <span
                                                            className="flex items-center text-red-600 text-sm cursor-pointer absent-bg"
                                                            onClick={() => handleMarkPresent(p.id)}
                                                        >
                                                            Absent
                                                            <img src={closeIcon} className="w-5 h-5 cursor-pointer" alt="" />
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>

                                    <div className="flex justify-between w-full my-[16px]  items-center mq991:grid">
                                        <h2 className="font-semibold mb-3">External Participants</h2>
                                        <p className="rounded-[50px] bg-antiquewhite border-whitesmoke w-fit mq991:py-2 border-solid border-[1px] box-border flex flex-row items-center justify-center  px-4 text-base blue_status_bg_dv">
                                            <p className="text-sm text-gray-500 my-0"> {externalParticipants.filter((p) => p.is_attended).length}/{externalParticipants.length} present</p>
                                        </p>
                                    </div>
                                    <div className="grid items-center py-[0px]  justify-between border-[1px] border-solid border-surface-border rounded cursor-pointer bg-white w-full">
                                        {externalParticipants.map((p) => (
                                            <div key={p.id} className="flex items-center justify-between py-2 w-full">
                                                <div className="block flex-nowrap ga-[8px]">
                                                    <p className="font-medium text-gray-800 my-0">{p.name}</p>
                                                    <p className="text-sm text-gray-500 my-0">Email: {p.email}</p>
                                                    {p.absent_note && (
                                                        <p className="text-xs text-red-500 italic">Note: {p.absent_note}</p>
                                                    )}
                                                </div>

                                                {p.is_attended ? (
                                                    <span
                                                        className="flex items-center justify-end text-green-600 text-sm cursor-pointer  present-bg"
                                                        onClick={() => handleMarkExternalAbsent(p)}
                                                    >
                                                        Present <img src={checkIcon} className="w-5 h-5 cursor-pointer" alt="" />
                                                        {/* click to mark absent */}
                                                    </span>
                                                ) : (
                                                    <span
                                                        className="flex items-center justify-end text-red-600 text-sm cursor-pointer absent-bg"
                                                        onClick={() => handleMarkExternalPresent(p.id)}
                                                    >
                                                        Absent <img src={closeIcon} className="w-5 h-5 cursor-pointer" alt="" />
                                                        {/* click to mark present */}
                                                    </span>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>

                            {/* Buttons */}
                            {((isHost) && (meeting?.is_email_send !== true)) && (
                                // {(isHost) && (
                                <div className="flex justify-end mt-6 space-x-3 w-full">
                                    <button
                                        type="button"
                                        onClick={() => navigate(-1)}
                                        className="cursor-pointer bg-white py-2.5 h-[44px] mq575:h-[35px] px-[42px] btn-text-purple mq575:py-[12.5px] mq575:px-[25px] rounded-radiuscomponent-button box-border flex flex-row items-center justify-center gap-[12px] border-[2px] border-solid border-surface-primary-surface hover:bg-gainsboro-100 hover:box-border hover:border-[2px] hover:border-solid hover:border-dodgerblue"
                                    >
                                        <span className="relative text-sm font-body-xs-semi-bold  dark:text-white text-center inline-block">Cancel</span>
                                    </button>
                                    <button className="cursor-pointer [border:none] py-2.5 h-[44px] mq575:h-[35px] px-[42px] mq575:py-[12.5px] mq575:px-[25px] bg-surface-primary-surface  flex flex-row items-center justify-center gap-[12px] hover:bg-dodgerblue">
                                        <span className="relative text-sm font-body-xs-semi-bold text-surface-bg-primary text-center inline-block">Save</span>
                                    </button>
                                    <button className="cursor-pointer [border:none] py-2.5 h-[44px] mq575:h-[35px] px-[42px] mq575:py-[12.5px] mq575:px-[25px] bg-surface-primary-surface flex flex-row items-center justify-center gap-[12px] hover:bg-dodgerblue"
                                        onClick={handleSubmit((data) => onSubmit(data, true))}
                                    >
                                        <span className="relative text-sm font-body-xs-semi-bold text-surface-bg-primary text-center inline-block">Save & Email</span>
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </form >
            {showModal && (
                <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center">
                    <div className="bg-white p-6 rounded-lg shadow-lg w-96 present-popup-blg">
                        <h4 className="text-xl font-semibold mb-4 mt-0">
                            Mark {selectedParticipant?.member_name || selectedExternalParticipant?.name} as Absent
                        </h4>
                        <textarea
                            value={absentNote}
                            onChange={(e) => setAbsentNote(e.target.value)}
                            placeholder="Enter absent note..."
                            className="w-[364px] border rounded-lg p-2 h-20 mb-4"
                        />
                        <div className="flex justify-end space-x-2">
                            <button
                                onClick={() => {
                                    setShowModal(false)
                                    setSelectedParticipant(null)
                                    setSelectedExternalParticipant(null)
                                }
                                }
                                className=" rounded-radiuscomponent-button bg-white box-border btn-text-purple  flex flex-row items-center justify-center dark-cancel-btn-bg  text-purple  py-2.5 h-[44px] mq575:h-[35px] px-[42px] mq575:py-[12.5px] mq575:px-[25px] gap-[12px] min-w-[88px] border-[2px] border-solid border-main-blue hover:bg-gainsboro-300 hover:box-border hover:border-[2px] hover:border-solid hover:border-dodgerblue cursor-pointer dark:text-white"
                            >
                                Cancel
                            </button>
                            {selectedParticipant?.member_name && (
                                <button
                                    type="button"
                                    onClick={saveAbsentNote}
                                    className="rounded-radiuscomponent-button bg-main-blue flex flex-row items-center justify-center py-2.5 h-[44px] mq575:h-[35px] px-[42px] mq575:py-[12.5px] mq575:px-[25px]  text-white hover:bg-dodgerblue cursor-pointer"
                                >
                                    Save
                                </button>
                            )}

                            {selectedExternalParticipant?.name && (
                                <button
                                    type="button"
                                    onClick={saveExternalAbsentNote}
                                    className="px-4 py-2 bg-red-600 text-white rounded-lg"
                                >
                                    Save
                                </button>
                            )}

                        </div>
                    </div>
                </div>
            )}

            {isDeletePopupOpen && (
                <DeleteMeeting
                    onsubmit={deleteMeetingFunction}
                    isOpen={isDeletePopupOpen}
                    onClose={closeDeletePopup}
                    date={meeting?.start_datetime}
                    isDelete={true}
                />
            )}

            {addTodoPopup && (
                <AddTodoPopup
                    isOpen={addTodoPopup}
                    onClose={() => { setAddTodoPopup(false); }}
                    refetch={refetchMeeting}
                    meetingID={meetingID}
                />
            )}

            {(loading || meetingActionsLoading || deleteloading || updateToDoLoading) && (
                <Loader />
            )}
        </>
    );
};

export default MeetingDetail;